// Dependencies
import React, { BaseSyntheticEvent, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import type * as Square from '@square/web-sdk'

// Internals
import useEvent from 'Hooks/useEvent'

import { useForm } from '../contexts/form'

import styles from './index.module.less'
import type { CreditCardBase } from './types'

/**
 * Renders a Credit Card Input to use in the Square Web Payment SDK, pre-styled
 * to meet Square branding guidelines.
 *
 * **_But with the option to override styles_**
 *
 * @example
 *
 * ```tsx
 * function App() {
 *   return (
 *     <SquareForm {...props}>
 *       <CreditCard />
 *     </SquareForm>
 *   );
 * }
 * ```
 */
const CreditCard: React.FC<CreditCardBase> = ({
  callbacks,
  focus = 'cardNumber',
  id = 'rswps-card-container',
  includeInputLabels,
  postalCode,
  cardResizeRef,
  setPaymentStatus = () => {},
  style,
  ...props
}) => {
  const [card, setCard] = useState<Square.Card | undefined>(() => undefined)
  const [attached, setAttached] = useState<boolean>(false)
  const { cardTokenizeResponseReceived, cardTokenizeRef, payments } = useForm()

  const options: Square.CardOptions = useMemo(() => {
    const baseOptions = {
      includeInputLabels,
      postalCode,
      style,
    }

    // if a value from options is undefined delete it from the options object
    return Object.keys(baseOptions).reduce((acc: Record<string, unknown>, key) => {
      if (baseOptions[key as keyof typeof baseOptions] !== undefined) {
        acc[key] = baseOptions[key as keyof typeof baseOptions]
      }

      return acc
    }, {})
  }, [includeInputLabels, postalCode, style])

  /**
   * Handle the on click of the Credit Card button click
   *
   * @param e An event which takes place in the DOM.
   * @returns The data be sended to `cardTokenizeResponseReceived()` function, or an error
   */
  const handlePayment = useEvent(async (e?: BaseSyntheticEvent) => {
    e?.stopPropagation()

    if (!card) {
      console.warn('Credit Card button was clicked, but no Credit Card instance was found.')

      return
    }

    try {
      const result = await card.tokenize()
      await cardTokenizeResponseReceived(result)
    } catch (error) {
      console.error(error)
    }
  })
  useImperativeHandle(cardTokenizeRef, () => handlePayment)
  useImperativeHandle(cardResizeRef, () => card?.recalculateSize.bind(card))

  useEffect(() => {
    const abortController = new AbortController()
    const { signal } = abortController

    const start = async (signal: AbortSignal) => {
      try {
        const card = await payments?.card(options).then((res) => {
          if (!signal.aborted) {
            setCard(res)
            return res
          }

          return null
        })

        await card?.attach(`#${id}`)
        setAttached(true)
        setPaymentStatus('ready')
        if (focus) {
          await card?.focus(focus)
        }

        if (signal.aborted) {
          await card?.destroy()
        }
      } catch (error) {
        console.error(error)
        setPaymentStatus('error')
      }
    }

    void start(signal)

    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, id])

  useEffect(() => {
    if (card && focus && attached) {
      void card.focus(focus)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, focus])

  if (callbacks) {
    for (const callback of Object.keys(callbacks)) {
      card?.addEventListener(
        callback as Square.CardInputEventTypes,
        (callbacks as Record<string, (event: Square.SqEvent<Square.CardInputEvent>) => void>)[callback],
      )
    }
  }

  return (
    <div {...props} id={id} style={{ minHeight: 89 }}>
      {card ? null : <div className={styles.loadingCard} />}
    </div>
  )
}

export default CreditCard
export * from './types'
